import { Injectable } from '@angular/core';
import { AngularFirestore, DocumentChangeAction } from '@angular/fire/compat/firestore';
import { User } from '@core/models/user.model';
import { Observable } from 'rxjs';
import { mergeMap, take } from 'rxjs/operators';

/** How far back the chat listener reaches, to tolerate client/server clock skew. */
const CHAT_UPDATES_CLOCK_SKEW_MS = 5 * 60 * 1000;

@Injectable({
  providedIn: 'root',
})
export class FirebaseService {
  constructor(private firestore: AngularFirestore) {}

  getJobUpdates(orgId: string, jobRefId: string, transactionId: string) {
    const date = new Date();
    console.log(`job-updates/orgs/${orgId}/jobs/${jobRefId}/transactions/${transactionId} and date is ${date}`);
    return (
      this.firestore
        .collection(`job-updates`)
        .doc('orgs')
        .collection(orgId)
        .doc('jobs')
        .collection(jobRefId, ref => ref.where('timestamp', '>=', date))
        // .doc('transactions')
        // .collection(transactionId, ref => ref.where('timestamp', '>=', date))
        // .collection(transactionId)
        .stateChanges(['added', 'modified', 'removed'])
    );
  }

  getEntityUpdates(orgId: string, entityId: string) {
    const date = new Date();
    // console.log(`entity-updates/orgs/${orgId}/entities/${entityId} and date is ${date}`);
    return this.firestore
      .collection(`entity-updates`)
      .doc('orgs')
      .collection(orgId)
      .doc('entities')
      .collection(entityId, ref => ref.where('timestamp', '>=', date))
      .stateChanges(['added', 'modified', 'removed']);
  }

  getGenericEntityUpdates(entityType: string, entityId: string) {
    const date = new Date();
    return this.firestore
      .collection(`entity-updates`)
      .doc(entityType)
      .collection(entityId, ref => ref.where('timestamp', '>=', date))
      .stateChanges(['added', 'modified', 'removed']);
  }

  getEventFeedEventsChanges(orgId: string, controlTowerId: string): Observable<DocumentChangeAction<unknown>[]> {
    const basePath = `entity-updates/orgs/${orgId}/entities/${controlTowerId}/event-feed/events`;
    const date = new Date();
    return this.firestore
      .collection(basePath, ref => ref.where('timestamp', '>=', date))
      .stateChanges(['added', 'modified', 'removed']);
  }

  getNotifications(orgId: string, jobRefId: string) {
    const date = new Date();
    return this.firestore
      .collection(orgId)
      .doc('JOB_UPDATES')
      .collection(jobRefId, ref => ref.where('createdAt', '>=', date))
      .stateChanges();
    // .snapshotChanges();
  }

  fetchMoreNotifications(notificationLimit: number, user: User) {
    const first = this.firestore
      .collection(user.orgId)
      .doc('PERSONAL_NOTIFICATIONS')
      .collection(user.userId, ref => ref.orderBy('createdAt').limit(notificationLimit));
    return first.get().pipe(
      take(1),
      mergeMap(documentSnapshots => {
        const lastOne = documentSnapshots.docs[documentSnapshots.docs.length - 1];
        const x = this.firestore
          .collection(user.orgId)
          .doc('PERSONAL_NOTIFICATIONS')
          .collection(user.userId, ref => ref.orderBy('createdAt', 'desc').startAfter(lastOne).limit(notificationLimit))
          .snapshotChanges();
        return x;
      })
    );
  }

  getInitialNotifications(notificationLimit: number, user: User) {
    return this.firestore
      .collection(user.orgId)
      .doc('PERSONAL_NOTIFICATIONS')
      .collection(user.userId, ref => ref.orderBy('createdAt', 'desc').limit(notificationLimit))
      .stateChanges();
  }

  getDsrEventsChanges(orgId: string, controlTowerId: string): Observable<DocumentChangeAction<unknown>[]> {
    const basePath = `entity-updates/orgs/${orgId}/entities/${controlTowerId}/daily-status-report/events`;
    const date = new Date();
    return this.firestore
      .collection(basePath, ref => ref.where('timestamp', '>=', date))
      .stateChanges(['added', 'modified', 'removed']);
  }

  getTrackingInfo(shipmentId: string, controlTowerId: string) {
    const path = `shipment-tracking/${shipmentId}/tracking-info/${controlTowerId}`;
    return this.firestore.doc(path).valueChanges();
  }

  getBidNegotiationUpdates(bidId: string) {
    const date = new Date();
    return this.firestore
      .collection(`entity-updates`)
      .doc('BID_NEGOTIATION')
      .collection(bidId, ref => ref.where('timestamp', '>=', date))
      .stateChanges(['added', 'modified', 'removed']);
  }

  /**
   * One path per chat channel (one buyer/supplier pair), so a supplier only
   * ever subscribes to their own thread. Pushes carry ids, not content.
   * Shared by Pre Communication and negotiation's chat-based option.
   */
  getChatChannelUpdates(channelId: string) {
    // `timestamp` is stamped by the server, so a client clock running ahead
    // would silently drop pushes. Start a little earlier; the extra docs only
    // cause a refetch of messages the chat already de-duplicates by id.
    const date = new Date(Date.now() - CHAT_UPDATES_CLOCK_SKEW_MS);
    return this.firestore
      .collection(`entity-updates`)
      .doc('negotiations')
      .collection(channelId, ref => ref.where('timestamp', '>=', date))
      .stateChanges(['added', 'modified', 'removed']);
  }
}
